import {
	FunctionComponent,
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	ContentWithSidebar,
	Dialog,
	Form,
	renderReactDialog,
	SidebarHandle,
	Toolbar,
	TreeView,
	TreeViewItem
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { sortBy, unique } from "client_legacy/ndaw/arrayHelpers";
import { localize } from "client_legacy/ndaw/culture";
import {
	MutedActivityFilterModel,
	NotificationKind,
	NotificationViewModel,
	CustomNotification,
	MutedActivityFilterSaveModel,
	setMuteFilters,
	MutedActivityFilterKind,
	MutedByCustomTagActivityFilterModel,
	appendMuteFilters,
	MutedActivityFilterCollectionSaveModel,
	listMuteFilters,
	MutedActivityFilterCollectionModel
} from "client/Api/Notifications/notifications";
import { useActivityFeedStore } from "../store";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useStableImperativeCallback,
	useSynchronizedRef,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { normalizeCustomActivityTag } from "../stacks";
import { BriefingEmailSection, EmailSettingsState } from "./BriefingEmailSection";
import { AppActivitySection } from "./AppActivitySection";
import { BirthdayActivitySection } from "./BirthdayActivitySection";
import { CalendarActivitySection } from "./CalendarActivitySection";
import { EmailActivitySection } from "./EmailActivitySection";
import { DocumentActivitySection } from "./DocumentActivitySection";
import { HubActivitySection } from "./HubActivitySection";
import { ExternalActivitySection } from "./ExternalActivitySection";
import { isNotNil } from "client/Misc/utils";

export type NotificationSettingsDialogProps = {
	onClose(): void;
};

export type Item = {
	isEnabled: boolean;
};

export type CustomActivityItem = {
	tag: string;
	isEnabled: boolean;
};

export type State = ActivityFeedSettingsState & EmailSettingsState;

export const NotificationSettingsDialog = (props: NotificationSettingsDialogProps): ReactElement => {
	const { onClose } = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [selectedNodeId, setSelectedNodeId] = useState<NodeId>("apps");
	const sidebarRef: RefObject<SidebarHandle | null> = useRef<SidebarHandle>(null);

	const activities: ReadonlyArray<NotificationViewModel> = useActivityFeedStore(s => s.activities);
	const loadFilters: (() => Promise<void>) = useActivityFeedStore(s => s.loadFilters);
	const didInitiallyLoadFromBackend: boolean = useActivityFeedStore(x => x.didInitiallyLoadFromBackend);

	const [state, setState] = useState<State>(() => getInitialState(activities, []));
	const [filtersConfigurationSettings, setFiltersConfigurationSettings] = useState<FiltersConfigurationSettings>({
		canConfigureCommunityFilters: false,
		canConfigureEmailFilters: false
	});
	const formHandler: IFormHandler<State> = useFormHandler(state, setState, "NoValidation");
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const {
		canConfigureCommunityFilters,
		canConfigureEmailFilters
	} = filtersConfigurationSettings;

	const hasCustomActivityFilters: boolean = Boolean(state.customActivitiesWithoutTags)
		|| state.customActivityTags.length > 0;

	const sidebarItems: readonly TreeViewItem<NodeId>[] = useSidebarItems(
		hasCustomActivityFilters,
		canConfigureEmailFilters
	);

	// We don't want to rerun the loading effect when the activities on onClose changes, but we do need the activities
	// for the custom notification ("External") tags
	const activitiesRef: ReadonlyRefObject<readonly NotificationViewModel[]> = useSynchronizedRef(activities);
	const stableOnClose: typeof onClose = useStableImperativeCallback(onClose);

	useEffect(
		(): (() => void) | undefined => {
			setIsBusy(true);

			if (!didInitiallyLoadFromBackend) {
				return;
			}

			const load = async (signal: AbortSignal): Promise<void> => {
				try {
					const model: MutedActivityFilterCollectionModel = await listMuteFilters(signal);

					setState(
						getInitialState(activitiesRef.current, model.filters)
					);

					setFiltersConfigurationSettings({
						canConfigureEmailFilters: model.canConfigureEmailFilters,
						canConfigureCommunityFilters: model.canConfigureCommunityFilters
					});

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					stableOnClose();
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return () => cancellation.abort();
		},
		[
			activitiesRef,
			alertManager,
			didInitiallyLoadFromBackend,
			stableOnClose
		]

	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			setIsBusy(true);
			try {
				const filters: ReadonlyArray<MutedActivityFilterSaveModel> = getMuteFiltersFromState(
					state,
					canConfigureEmailFilters
				);

				const model: MutedActivityFilterCollectionSaveModel = {
					filters
				};
				await setMuteFilters(model, globalCancellation);
				await loadFilters();
				setIsBusy(false);
				onClose();
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			canConfigureEmailFilters,
			globalCancellation,
			loadFilters,
			onClose,
			state
		]
	);

	const changeSelectedNodeHandler = useCallback(
		(id: NodeId): void => {
			if (isMobile) {
				sidebarRef.current
					?.onClose();
			}

			setSelectedNodeId(id);
		},
		[isMobile]
	);

	const nodeIdToComponentMap = useMemo(
		() => {
			return getNodeIdToComponentMap(canConfigureCommunityFilters);
		},
		[canConfigureCommunityFilters]
	);

	const ContentComponent: FunctionComponent<{ formHandler: IFormHandler<State>; }> | null = nodeIdToComponentMap[selectedNodeId];

	return (
		<Dialog
			trackingId="notification-settings-dialog"
			hasBusyIndicator={isBusy}
			variant="left-slider"
			onClose={onClose}
		>
			<Dialog.Header
				title={localize("_NotificationSettings")}
				onClose={onClose}
				hasBorder
			/>
			<Dialog.Body goesEdgeToEdge>
				<ContentWithSidebar>
					<ContentWithSidebar.Sidebar ref={sidebarRef}>
						<TreeView<NodeId>
							value={selectedNodeId}
							onChange={changeSelectedNodeHandler}
							items={sidebarItems}
							areContainersSelectable={false}
							initiallyExpandedNodes={["activity-feed-header", "email-header"]}
						/>

					</ContentWithSidebar.Sidebar>

					<ContentWithSidebar.Content>
						<Form onSubmit={saveHandler}>
							{ContentComponent && (
								<ContentComponent formHandler={formHandler} />
							)}
						</Form>
					</ContentWithSidebar.Content>
				</ContentWithSidebar>
			</Dialog.Body>
			<Dialog.Footer hasBorder>
				<Toolbar>
					<Toolbar.Button
						trackingId="save"
						kind="primary"
						text={localize("_Save")}
						onClick={saveHandler}
					/>
					<Toolbar.Button
						trackingId="cancel"
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

NotificationSettingsDialog.displayName = "NotificationSettingsDialog";

type FiltersConfigurationSettings = {
	canConfigureEmailFilters: boolean;
	canConfigureCommunityFilters: boolean;
};

export function showNotificationSettingsDialog(props: Omit<NotificationSettingsDialogProps, "onClose"> = {}): void {
	renderReactDialog(onClose => <NotificationSettingsDialog {...props} onClose={onClose} />);
}

function getInitialState(
	activities: ReadonlyArray<NotificationViewModel>,
	currentFilters: ReadonlyArray<MutedActivityFilterModel>
): State {
	const customActivities: CustomNotification[] = activities
		.filter(a => a.kind === NotificationKind.Custom) as CustomNotification[];

	const existingCustomActivityTags: (string | null)[] = customActivities.map(a => a.data.tag);

	const customActivityFilters: MutedByCustomTagActivityFilterModel[] = unique(
		currentFilters.filter(f => f.kind === MutedActivityFilterKind.ByCustomActivityTag) as MutedByCustomTagActivityFilterModel[],
		f => normalizeCustomActivityTag(f.tag)
	);

	let customActivityTagItems: CustomActivityItem[] = customActivityFilters
		.map(
			f => ({
				tag: f.tag,
				isEnabled: false
			})
		);

	for (const tag of existingCustomActivityTags) {
		if (tag && !customActivityTagItems.some(i => normalizeCustomActivityTag(i.tag) === normalizeCustomActivityTag(tag))) {
			customActivityTagItems.push({
				tag,
				isEnabled: true
			});
		}
	}

	customActivityTagItems = sortBy(customActivityTagItems, x => x.tag);

	const hasFilterForKind = (kind: NotificationKind): boolean => {
		return currentFilters.some(
			k => k.kind === MutedActivityFilterKind.ByActivityKind
				&& k.activityKind === kind
		);
	};

	const createItemFor = (kind: NotificationKind): Item => {
		return {
			isEnabled: !hasFilterForKind(kind)
		};
	};

	const createNewAvailableAppItem = (): Item => {
		const hasFilter: boolean = hasFilterForKind(NotificationKind.NewMainAppPromotion)
			|| hasFilterForKind(NotificationKind.AppAccess);

		return {
			isEnabled: !hasFilter
		};
	};

	const createAppointmentItem = (): Item => {
		const hasFilter: boolean = currentFilters
			.some(f => f.kind === MutedActivityFilterKind.Appointment);

		return {
			isEnabled: !hasFilter
		};
	};

	const createDailyBriefingEmailItem = (): Item => {
		const hasFilter: boolean = currentFilters
			.some(f => f.kind === MutedActivityFilterKind.DailyBriefingEmail);

		return {
			isEnabled: !hasFilter
		};
	};

	const createWeeklyBriefingEmailItem = (): Item => {
		const hasFilter: boolean = currentFilters
			.some(f => f.kind === MutedActivityFilterKind.WeeklyBriefingEmail);

		return {
			isEnabled: !hasFilter
		};
	};

	const createAnnouncementReactionItem = (): Item => {
		const hasFilter: boolean = hasFilterForKind(NotificationKind.AnnouncementReaction)
			|| hasFilterForKind(NotificationKind.AnnouncementCommentReaction);

		return {
			isEnabled: !hasFilter
		};
	};

	const createCommunityReactionItem = (): Item => {
		const hasFilter: boolean = hasFilterForKind(NotificationKind.CommunityPostReaction)
			|| hasFilterForKind(NotificationKind.CommunityPostCommentReaction);

		return {
			isEnabled: !hasFilter
		};
	};

	const createEventReactionItem = (): Item => {
		const hasFilter: boolean = hasFilterForKind(NotificationKind.EventReaction)
			|| hasFilterForKind(NotificationKind.EventCommentReaction);

		return {
			isEnabled: !hasFilter
		};
	};

	const createKnowledgeItemReactionItem = (): Item => {
		const hasFilter: boolean = hasFilterForKind(NotificationKind.KnowledgeItemReaction)
			|| hasFilterForKind(NotificationKind.KnowledgeItemCommentReaction);

		return {
			isEnabled: !hasFilter
		};
	};

	let customActivitiesWithoutTag: Item | null;
	if (currentFilters.some(f => f.kind === MutedActivityFilterKind.CustomActivityWithoutTag)) {
		customActivitiesWithoutTag = {
			isEnabled: false
		};
	} else if (existingCustomActivityTags.some(tag => !tag)) {
		customActivitiesWithoutTag = {
			isEnabled: true
		};
	} else {
		customActivitiesWithoutTag = null;
	}

	return {
		customActivityTags: customActivityTagItems,
		announcements: createItemFor(NotificationKind.Announcement),
		events: createItemFor(NotificationKind.Event),
		knowledgeItem: createItemFor(NotificationKind.KnowledgeItem),
		communityPosts: createItemFor(NotificationKind.CommunityPost),
		communityPostComments: createItemFor(NotificationKind.CommunityPostComment),
		communityPostCommentMentions: createItemFor(NotificationKind.CommunityPostCommentMention),
		community: createItemFor(NotificationKind.Community),
		communityMembership: createItemFor(NotificationKind.CommunityMembership),
		communityReactions: createCommunityReactionItem(),
		eventAttendances: createItemFor(NotificationKind.EventAttendance),
		announcementComments: createItemFor(NotificationKind.AnnouncementComment),
		maintenances: createItemFor(NotificationKind.Maintenance),
		userRequests: createItemFor(NotificationKind.UserRequest),
		birthdays: createItemFor(NotificationKind.Birthday),
		birthdayWishes: createItemFor(NotificationKind.BirthdayWish),
		birthdayWishReactions: createItemFor(NotificationKind.BirthdayWishReaction),
		calendarEvents: createAppointmentItem(),
		dailyBriefingEmail: createDailyBriefingEmailItem(),
		weeklyBriefingEmail: createWeeklyBriefingEmailItem(),
		newEmails: createItemFor(NotificationKind.NewEmail),
		crossStoreOperations: createItemFor(NotificationKind.CrossStoreOperation),
		documentShared: createItemFor(NotificationKind.DocumentShared),
		newAvailableApps: createNewAvailableAppItem(),
		customActivitiesWithoutTags: customActivitiesWithoutTag,
		announcementReactions: createAnnouncementReactionItem(),
		announcementCommentMentions: createItemFor(NotificationKind.AnnouncementCommentMention),
		eventReactions: createEventReactionItem(),
		eventComments: createItemFor(NotificationKind.EventComment),
		eventCommentMentions: createItemFor(NotificationKind.EventCommentMention),
		knowledgeItemComments: createItemFor(NotificationKind.KnowledgeItemComment),
		knowledgeItemCommentMentions: createItemFor(NotificationKind.KnowledgeItemCommentMention),
		knowledgeItemReactions: createKnowledgeItemReactionItem()
	};
}

function getMuteFiltersFromState(
	state: State,
	canConfigureEmailFilters: boolean
): ReadonlyArray<MutedActivityFilterSaveModel> {
	const result: MutedActivityFilterSaveModel[] = [];

	const {
		announcements,
		knowledgeItem,
		announcementComments,
		eventAttendances,
		birthdays,
		birthdayWishes,
		calendarEvents,
		crossStoreOperations,
		documentShared,
		customActivityTags,
		maintenances,
		newAvailableApps,
		newEmails,
		userRequests,
		customActivitiesWithoutTags,
		dailyBriefingEmail,
		birthdayWishReactions,
		events,
		announcementReactions,
		weeklyBriefingEmail,
		announcementCommentMentions,
		communityPostComments,
		communityReactions,
		communityPostCommentMentions,
		communityPosts,
		community,
		communityMembership,
		eventReactions,
		eventComments,
		eventCommentMentions,
		knowledgeItemComments,
		knowledgeItemCommentMentions,
		knowledgeItemReactions
	} = state;

	const applyItemToResult = (item: Item, activityKind: NotificationKind): void => {
		if (item.isEnabled) {
			return;
		}

		result.push({
			kind: MutedActivityFilterKind.ByActivityKind,
			byActivityKindData: {
				activityKind
			}
		});
	};

	applyItemToResult(announcements, NotificationKind.Announcement);
	applyItemToResult(knowledgeItem, NotificationKind.KnowledgeItem);
	applyItemToResult(announcementComments, NotificationKind.AnnouncementComment);
	applyItemToResult(announcementCommentMentions, NotificationKind.AnnouncementCommentMention);
	applyItemToResult(eventAttendances, NotificationKind.EventAttendance);
	applyItemToResult(communityPostComments, NotificationKind.CommunityPostComment);
	applyItemToResult(communityPostCommentMentions, NotificationKind.CommunityPostCommentMention);
	applyItemToResult(communityPosts, NotificationKind.CommunityPost);
	applyItemToResult(community, NotificationKind.Community);
	applyItemToResult(communityMembership, NotificationKind.CommunityMembership);
	applyItemToResult(birthdays, NotificationKind.Birthday);
	applyItemToResult(birthdayWishes, NotificationKind.BirthdayWish);
	applyItemToResult(birthdayWishReactions, NotificationKind.BirthdayWishReaction);
	applyItemToResult(crossStoreOperations, NotificationKind.CrossStoreOperation);
	applyItemToResult(documentShared, NotificationKind.DocumentShared);
	applyItemToResult(maintenances, NotificationKind.Maintenance);
	applyItemToResult(newEmails, NotificationKind.NewEmail);
	applyItemToResult(userRequests, NotificationKind.UserRequest);
	applyItemToResult(events, NotificationKind.Event);
	applyItemToResult(eventComments, NotificationKind.EventComment);
	applyItemToResult(eventCommentMentions, NotificationKind.EventCommentMention);
	applyItemToResult(knowledgeItemComments, NotificationKind.KnowledgeItemComment);
	applyItemToResult(knowledgeItemCommentMentions, NotificationKind.KnowledgeItemCommentMention);

	if (!calendarEvents.isEnabled) {
		result.push({
			kind: MutedActivityFilterKind.Appointment
		});
	}

	if (canConfigureEmailFilters && !dailyBriefingEmail.isEnabled) {
		result.push({
			kind: MutedActivityFilterKind.DailyBriefingEmail
		});
	}

	if (canConfigureEmailFilters && !weeklyBriefingEmail.isEnabled) {
		result.push({
			kind: MutedActivityFilterKind.WeeklyBriefingEmail
		});
	}

	if (!newAvailableApps.isEnabled) {
		result.push(
			{
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind: NotificationKind.NewMainAppPromotion
				}
			}, {
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind: NotificationKind.AppAccess
				}
			}
		);
	}

	if (!announcementReactions.isEnabled) {
		result.push(
			{
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind: NotificationKind.AnnouncementCommentReaction
				}
			}, {
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind: NotificationKind.AnnouncementReaction
				}
			}
		);
	}

	if (!communityReactions.isEnabled) {
		result.push(
			{
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind: NotificationKind.CommunityPostReaction
				}
			}, {
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind: NotificationKind.CommunityPostCommentReaction
				}
			}
		);
	}

	if (!eventReactions.isEnabled) {
		result.push(
			{
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind: NotificationKind.EventReaction
				}
			},
			{
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind: NotificationKind.EventCommentReaction
				}
			}
		);
	}

	if (!knowledgeItemReactions.isEnabled) {
		result.push(
			{
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind: NotificationKind.KnowledgeItemReaction
				}
			},
			{
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind: NotificationKind.KnowledgeItemCommentReaction
				}
			}
		);
	}

	for (const tagItem of customActivityTags) {
		if (tagItem.isEnabled) {
			continue;
		}

		result.push({
			kind: MutedActivityFilterKind.ByCustomActivityTag,
			byCustomActivityTagData: {
				tag: tagItem.tag
			}
		});
	}

	if (customActivitiesWithoutTags && !customActivitiesWithoutTags.isEnabled) {
		result.push({
			kind: MutedActivityFilterKind.CustomActivityWithoutTag
		});
	}

	return result;
}

export function muteAppointment(signal?: AbortSignal): Promise<void> {
	const model: MutedActivityFilterCollectionSaveModel = {
		filters: [
			{ kind: MutedActivityFilterKind.Appointment }
		]
	};

	return appendMuteFilters(model, signal);
}

export function muteCustomActivityTag(tag: string, signal?: AbortSignal): Promise<void> {
	const model: MutedActivityFilterCollectionSaveModel = {
		filters: [
			{
				kind: MutedActivityFilterKind.ByCustomActivityTag,
				byCustomActivityTagData: { tag }
			}
		]
	};

	return appendMuteFilters(model, signal);
}

export function muteCustomActivityWithoutTag(signal?: AbortSignal): Promise<void> {
	const model: MutedActivityFilterCollectionSaveModel = {
		filters: [
			{
				kind: MutedActivityFilterKind.CustomActivityWithoutTag
			}
		]
	};

	return appendMuteFilters(model, signal);
}

export function muteNewApps(signal?: AbortSignal): Promise<void> {
	const model: MutedActivityFilterCollectionSaveModel = {
		filters: [
			{
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind: NotificationKind.NewMainAppPromotion
				}
			},
			{
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind: NotificationKind.AppAccess
				}
			}
		]
	};
	return appendMuteFilters(model, signal);
}

type ActivityKindWhichCanBeMuted =
	| NotificationKind.Announcement
	| NotificationKind.AnnouncementComment
	| NotificationKind.Birthday
	| NotificationKind.BirthdayWish
	| NotificationKind.BirthdayWishReaction
	| NotificationKind.CrossStoreOperation
	| NotificationKind.Maintenance
	| NotificationKind.NewEmail
	| NotificationKind.UserRequest
	| NotificationKind.DocumentShared
	| NotificationKind.KnowledgeItem
	| NotificationKind.Event
	| NotificationKind.EventAttendance
	| NotificationKind.AnnouncementCommentMention
	| NotificationKind.CommunityPost
	| NotificationKind.CommunityPostComment
	| NotificationKind.CommunityPostCommentMention
	| NotificationKind.Community
	| NotificationKind.CommunityMembership
	| NotificationKind.EventComment
	| NotificationKind.EventCommentMention
	| NotificationKind.KnowledgeItemComment
	| NotificationKind.KnowledgeItemCommentMention;

export function muteActivityKind(activityKind: ActivityKindWhichCanBeMuted, signal?: AbortSignal): Promise<void> {
	const model: MutedActivityFilterCollectionSaveModel = {
		filters: [
			{
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind
				}
			}
		]
	};

	return appendMuteFilters(model, signal);
}

export function muteAnnouncementReactions(signal?: AbortSignal): Promise<void> {
	const model: MutedActivityFilterCollectionSaveModel = {
		filters: [
			{
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind: NotificationKind.AnnouncementCommentReaction
				}
			},
			{
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind: NotificationKind.AnnouncementReaction
				}
			}
		]
	};

	return appendMuteFilters(model, signal);
}

export function muteCommunityReactions(signal?: AbortSignal): Promise<void> {
	const model: MutedActivityFilterCollectionSaveModel = {
		filters: [
			{
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind: NotificationKind.CommunityPostReaction
				}
			},
			{
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind: NotificationKind.CommunityPostCommentReaction
				}
			}
		]
	};

	return appendMuteFilters(model, signal);
}

export function muteEventReactions(signal?: AbortSignal): Promise<void> {
	const model: MutedActivityFilterCollectionSaveModel = {
		filters: [
			{
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind: NotificationKind.EventReaction
				}
			},
			{
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind: NotificationKind.EventCommentReaction
				}
			}
		]
	};

	return appendMuteFilters(model, signal);
}

export const muteKnowledgeItemReactions = (signal?: AbortSignal): Promise<void> => {
	const model: MutedActivityFilterCollectionSaveModel = {
		filters: [
			{
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind: NotificationKind.KnowledgeItemReaction
				}
			},
			{
				kind: MutedActivityFilterKind.ByActivityKind,
				byActivityKindData: {
					activityKind: NotificationKind.KnowledgeItemCommentReaction
				}
			}
		]
	};
	return appendMuteFilters(model, signal);
};

type HocHubActivitySectionProps = {
	formHandler: IFormHandler<State>;
};

const buildHubActivitySection = (
	canConfigureCommunityFilters: boolean
): FunctionComponent<HocHubActivitySectionProps> => {
	const HocHubActivitySection = (props: HocHubActivitySectionProps): ReactElement => {
		const {
			formHandler
		} = props;

		return (
			<HubActivitySection
				formHandler={formHandler}
				canConfigureCommunityFilters={canConfigureCommunityFilters}
			/>
		);
	};

	HocHubActivitySection.displayName = "HocHubActivitySection";
	return HocHubActivitySection;
};

type NodeId =
	| "activity-feed-header"
	| "apps"
	| "birthdays"
	| "calendar"
	| "email"
	| "folders-and-files"
	| "hub"
	| "external"
	| "email-header"
	| "briefing";

const getNodeIdToComponentMap = (
	canConfigureCommunityFilters: boolean
): Record<NodeId, FunctionComponent<{ formHandler: IFormHandler<State>; }> | null> => ({
	"activity-feed-header": null,
	"apps": AppActivitySection,
	"birthdays": BirthdayActivitySection,
	"calendar": CalendarActivitySection,
	"email": EmailActivitySection,
	"folders-and-files": DocumentActivitySection,
	"hub": buildHubActivitySection(canConfigureCommunityFilters),
	"external": ExternalActivitySection,
	"email-header": null,
	"briefing": BriefingEmailSection
});

function useSidebarItems(
	hasCustomActivityFilters: boolean,
	canConfigureEmailFilters: boolean
): readonly TreeViewItem<NodeId>[] {
	return useMemo(
		(): readonly TreeViewItem<NodeId>[] => {
			return [
				{
					content: localize("_ActivityFeed"),
					value: "activity-feed-header" as const,
					children: [
						{
							content: localize("_Apps"),
							value: "apps" as const
						},
						{
							content: localize("_Birthdays"),
							value: "birthdays" as const
						},
						{
							content: localize("_Calendar"),
							value: "calendar" as const
						},
						{
							content: localize("_Email"),
							value: "email" as const
						},
						{
							content: localize("_FoldersAndFiles"),
							value: "folders-and-files" as const
						},
						{
							content: localize("_TheHub"),
							value: "hub" as const
						},
						hasCustomActivityFilters
							? {
								content: localize("_External"),
								value: "external" as const
							}
							: null
					]
						.filter(isNotNil)
				},
				canConfigureEmailFilters
					?
					{
						content: localize("_Email"),
						value: "email-header" as const,
						children: [
							{
								content: localize("_Summary"),
								value: "briefing" as const
							}
						]
					}
					: null
			]
				.filter(isNotNil);
		},
		[canConfigureEmailFilters, hasCustomActivityFilters]
	);
}

export type ActivityFeedSettingsState = {
	announcements: Item;
	events: Item;
	knowledgeItem: Item;
	communityPosts: Item;
	communityPostComments: Item;
	communityReactions: Item;
	communityPostCommentMentions: Item;
	community: Item;
	communityMembership: Item;
	announcementComments: Item;
	eventAttendances: Item;
	newAvailableApps: Item;
	maintenances: Item;
	userRequests: Item;
	birthdays: Item;
	birthdayWishes: Item;
	birthdayWishReactions: Item;
	calendarEvents: Item;
	newEmails: Item;
	crossStoreOperations: Item;
	documentShared: Item;
	customActivitiesWithoutTags: Item | null;
	announcementReactions: Item;
	customActivityTags: ReadonlyArray<CustomActivityItem>;
	announcementCommentMentions: Item;
	eventReactions: Item;
	eventComments: Item;
	eventCommentMentions: Item;
	knowledgeItemComments: Item;
	knowledgeItemCommentMentions: Item;
	knowledgeItemReactions: Item;
};
